import React from 'react';

/*
 * The pivot of the page: what we actually sell.
 *
 * Everything above this is the problem. Everything below elaborates one of
 * these three columns — the silencer section expands Deploy, the guarantee
 * section expands Measure and Guarantee.
 *
 * The argument is deliberately NOT "our hardware is better". The failure cases
 * in the newsreel above were hardware purchases with nobody on the hook for the
 * result, so leading with a better product would be answering the wrong
 * question. What is different here is the structure: the performance risk sits
 * with us and it is written down.
 *
 * That is also why this section carries no dB figure. It doesn't need one — the
 * closing line makes a virtue of not asking anyone to believe one.
 *
 * Surface: paper. Per the site's loud/quiet rule, the dark bands are the
 * problem side; this is the solution side and stays on paper. The accent rules
 * above each column head carry the emphasis instead.
 */

const COLUMNS = [
  {
    n: '01',
    title: 'Deploy',
    body: 'Every route out of a plant is either a solid boundary or an opening that has to stay open for air. A screen handles the first, and we supply it at cost. The tone leaves through the second. That is our own low-frequency silencer, an element we designed because nothing on the market covers that band, or follows the tone as it moves with fan speed.',
  },
  {
    n: '02',
    title: 'Measure',
    body: 'A certified Class 1 monitor at the compliance point, and one of our own sensors on every machine, so an exceedance can be attributed rather than argued about.',
  },
  {
    n: '03',
    title: 'Guarantee',
    body: 'A written SLA on level and tonality, with a defined remedy ladder, fee credits, and a termination right.',
  },
];

function Offer() {
  return (
    <section className="section offer" id="offer">
      <div className="wrap">
        <p className="eyebrow">What we sell</p>
        <h2 className="h2 offer-title">
          We take responsibility for the result.
        </h2>
        {/* The lead names the tone here so the path split in Deploy lands as a
            consequence rather than a fact the reader has to take on trust. The
            50–125 Hz band is introduced two sections earlier, in the stake, and
            picked up again by the guarantee's band limits — this is the middle
            link in that chain and shouldn't be cut. */}
        <p className="lead offer-lead">
          Not a product you buy and hope about. The level at your property line,
          held to a written SLA, measured continuously, with the remedy at our
          cost. That includes the 50&ndash;125&nbsp;Hz tone the complaints are
          actually about.
        </p>

        <div className="offer-grid">
          {COLUMNS.map((c) => (
            <div className="offer-col" key={c.n}>
              <span className="offer-col-rule" aria-hidden="true" />
              <span className="offer-col-n">{c.n}</span>
              <h3 className="h3 offer-col-title">{c.title}</h3>
              <p className="offer-col-body">{c.body}</p>
            </div>
          ))}
        </div>

        <p className="offer-kicker">
          You are not being asked to believe our numbers. That is the point of
          the structure: the performance risk sits with us, and it is written
          down.
        </p>
      </div>
    </section>
  );
}

export default Offer;
